/**
 * Auth de ObrasControl — PIN + JWT propio.
 *
 * Reemplaza el MSAL del template oficial porque el personal de campo NO
 * tiene cuenta de Entra ID. Usa PIN, el backend firma un JWT propio y lo
 * guardamos en localStorage.
 *
 * Modo dev sigue funcionando igual: `VITE_AUTH_DEV_MODE=true` evita el
 * paso por login y el cliente HTTP omite el Authorization header.
 */

import type { RespuestaApi } from '@adelante/shared';

const API_URL = import.meta.env.VITE_API_URL;
const KEY_TOKEN = 'oc.auth.token';
const KEY_USUARIO = 'oc.auth.usuario';

export const modoDev = import.meta.env.DEV && import.meta.env.VITE_AUTH_DEV_MODE === 'true';

/** Usuario expuesto por el backend tras login. */
export interface UsuarioSesion {
  id: number;
  usuario: string;
  nombre: string;
  rol: string;
  telefono: string | null;
}

export const usuarioDev: UsuarioSesion = {
  id: 0,
  usuario: 'dev',
  nombre: 'Dev User',
  rol: 'admin',
  telefono: null,
};

interface LoginResponse {
  token: string;
  usuario: UsuarioSesion;
}

/** Iniciales (máx 2 letras) de un nombre. */
export function obtenerIniciales(nombre: string): string {
  return nombre
    .split(' ')
    .map((p) => p[0] ?? '')
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();
}

// =============================================================================
// Storage del token
// =============================================================================

export function obtenerToken(): string | null {
  if (modoDev) return null;
  try {
    return localStorage.getItem(KEY_TOKEN);
  } catch {
    return null;
  }
}

/** Decodifica el payload del JWT sin validar firma — solo para leer exp local. */
function leerPayloadJWT(token: string): { exp?: number } | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const payload = parts[1];
    if (!payload) return null;
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(json);
  } catch {
    return null;
  }
}

/** True si hay token y aún no expiró (chequeo local — el backend valida igual). */
export function tieneSesionActiva(): boolean {
  if (modoDev) return true;
  const t = obtenerToken();
  if (!t) return false;
  const payload = leerPayloadJWT(t);
  if (!payload?.exp) return false;
  return payload.exp * 1000 > Date.now();
}

export function obtenerUsuarioGuardado(): UsuarioSesion | null {
  if (modoDev) return { ...usuarioDev };
  try {
    const raw = localStorage.getItem(KEY_USUARIO);
    if (!raw) return null;
    return JSON.parse(raw) as UsuarioSesion;
  } catch {
    return null;
  }
}

// =============================================================================
// Login / Logout
// =============================================================================

/** Llama al backend con usuario + PIN. Guarda token y usuario en localStorage. */
export async function iniciarSesion(usuario: string, pin: string): Promise<UsuarioSesion> {
  const respuesta = await fetch(`${API_URL}/api/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ usuario, pin }),
  });

  const body = (await respuesta.json()) as RespuestaApi<LoginResponse>;

  if (!respuesta.ok || body.error) {
    const mensaje = body.error?.mensaje ?? `HTTP ${respuesta.status}`;
    throw new ErrorAuth(mensaje, body.error?.codigo);
  }

  if (!body.data) throw new ErrorAuth('Respuesta vacía del servidor');

  localStorage.setItem(KEY_TOKEN, body.data.token);
  localStorage.setItem(KEY_USUARIO, JSON.stringify(body.data.usuario));
  return body.data.usuario;
}

async function postAuth<T>(ruta: string, cuerpo: unknown): Promise<T> {
  const respuesta = await fetch(`${API_URL}${ruta}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(cuerpo),
  });
  const body = (await respuesta.json().catch(() => ({}))) as RespuestaApi<T>;
  if (!respuesta.ok || body.error) {
    throw new ErrorAuth(body.error?.mensaje ?? `HTTP ${respuesta.status}`, body.error?.codigo);
  }
  if (!body.data) throw new ErrorAuth('Respuesta vacía del servidor');
  return body.data;
}

/** Pide un código SMS al teléfono. Responde igual exista o no el número. */
export async function solicitarCodigoSms(telefono: string): Promise<{ expiraEnMin: number }> {
  return postAuth('/api/auth/otp/request', { telefono });
}

/** Verifica el código SMS. Guarda token y usuario en localStorage. */
export async function verificarCodigoSms(telefono: string, codigo: string): Promise<UsuarioSesion> {
  const data = await postAuth<LoginResponse>('/api/auth/otp/verify', { telefono, codigo });
  localStorage.setItem(KEY_TOKEN, data.token);
  localStorage.setItem(KEY_USUARIO, JSON.stringify(data.usuario));
  return data.usuario;
}

/** Limpia el token y datos del usuario del storage. */
export function cerrarSesion(): void {
  try {
    localStorage.removeItem(KEY_TOKEN);
    localStorage.removeItem(KEY_USUARIO);
  } catch {}
}

export class ErrorAuth extends Error {
  readonly codigo: string | undefined;
  constructor(mensaje: string, codigo?: string) {
    super(mensaje);
    this.name = 'ErrorAuth';
    this.codigo = codigo;
  }
}
